import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  saveToLocalStorage,
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToIndexedDB,
  removeFromIndexedDB,
} from "../../utils/storage";

interface User {
  id?: number | string;
  name: string;
  email: string;
  phone: string;
  businessName?: string;
  gstNo?: string;
  customerType?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  userGuid?: string;
  userName?: string;
  isFirstOrder?: boolean; // We'll call it isFirstOrder in the state for clarity
}

export interface LoginResponse {
  ID: number;
  FullName?: string; // For Customer
  Name?: string; // For User
  EmailID: string;
  MobileNo: string;
  BusinessName?: string; // For Customer
  CustomerType?: string; // For Customer
  Address?: string; // For Customer
  Pincode?: string; // For Customer
  UserGUID?: string; // For User
  UserName?: string; // For User
  Token?: string; // For User
  RefreshToken?: string; // For User
  IsFirstLogin?: boolean | string; // From API (boolean in password login, string "True"/"False" in OTP login)
  [key: string]: any;
}

interface AuthState {
  isUserAuthenticated: boolean;
  isCustomerAuthenticated: boolean;
  user: User | null;
  token: string | null;
  role: "user" | "customer" | null;
  loginData: LoginResponse | null;
}

const initialState: AuthState = {
  isUserAuthenticated:
    typeof window !== "undefined"
      ? getFromLocalStorage("nakman_role") === "user"
      : false,
  isCustomerAuthenticated:
    typeof window !== "undefined"
      ? getFromLocalStorage("nakman_role") === "customer"
      : false,
  user: null, // Will be hydrated or set on login
  token:
    typeof window !== "undefined"
      ? getFromLocalStorage("nakman_token") || null
      : null,
  role:
    typeof window !== "undefined"
      ? (getFromLocalStorage("nakman_role") as "user" | "customer" | null) ||
        null
      : null,
  loginData: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      const data = action.payload;
      const isUser = !!data.UserGUID; // Admin/User has UserGUID
      const role: "user" | "customer" = isUser ? "user" : "customer";

      // Cross-clearing logic: when logging in as admin, clear customer & vice versa
      if (typeof window !== "undefined") {
        // preserve cart for merging
        const cart = localStorage.getItem("nakman_cart");
        localStorage.clear();
        if (cart) localStorage.setItem("nakman_cart", cart);

        // Also clear IndexedDB keys related to the other role if any (optional but good)
        removeFromIndexedDB("nakman_user");
      }

      // Normalize IsFirstLogin from boolean (true/false) or string ("True"/"False"/"true"/"false")
      let isFirstLogin = true;
      if (typeof data.IsFirstLogin === "boolean") {
        isFirstLogin = data.IsFirstLogin;
      } else if (typeof data.IsFirstLogin === "string") {
        isFirstLogin = data.IsFirstLogin.trim().toLowerCase() === "true";
      } else if (
        data.IsFirstLogin !== undefined &&
        data.IsFirstLogin !== null
      ) {
        isFirstLogin = Boolean(data.IsFirstLogin);
      }

      state.role = role;
      state.loginData = {
        ...data,
        IsFirstLogin: isFirstLogin,
      };
      state.token = data.Token || null;

      if (isUser) {
        state.isUserAuthenticated = true;
        state.isCustomerAuthenticated = false;
        state.user = {
          id: data.ID,
          name: data.Name || "",
          userName: data.UserName || "",
          email: data.EmailID,
          phone: data.MobileNo,
          userGuid: data.UserGUID,
        };
      } else {
        state.isUserAuthenticated = false;
        state.isCustomerAuthenticated = true;
        state.user = {
          id: data.ID,
          name: data.FullName || "",
          email: data.EmailID,
          phone: data.MobileNo,
          businessName: data.BusinessName,
          customerType: data.CustomerType,
          address: data.Address,
          zipCode: data.Pincode,
          gstNo: data.GSTNo,
          isFirstOrder: isFirstLogin,
        };
      }

      if (typeof window !== "undefined") {
        saveToLocalStorage("nakman_role", role);
        if (data.Token) {
          saveToLocalStorage("nakman_token", data.Token);
          saveToLocalStorage("Token", data.Token);
        }
        if (data.RefreshToken) {
          saveToLocalStorage("RefreshToken", data.RefreshToken);
        }
        if (isUser && data.UserGUID)
          saveToLocalStorage("UserGUID", data.UserGUID);
        if (!isUser && data.ID) saveToLocalStorage("CustomerID", data.ID);
        saveToLocalStorage("nakman_user_data", state.user);
        saveToLocalStorage("IsFirstLogin", state.user?.isFirstOrder);
      }
    },
    logoutAll: (state) => {
      state.isUserAuthenticated = false;
      state.isCustomerAuthenticated = false;
      state.user = null;
      state.token = null;
      state.role = null;
      state.loginData = null;
      if (typeof window !== "undefined") {
        localStorage.clear();
        removeFromIndexedDB("nakman_user");
      }
    },
    hydrateAuth: (state, action: PayloadAction<Partial<AuthState>>) => {
      return { ...state, ...action.payload };
    },
    updateProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        if (typeof window !== "undefined") {
          saveToLocalStorage("nakman_user_data", state.user);
        }
      }
    },
  },
});

export const { loginSuccess, logoutAll, hydrateAuth, updateProfile } =
  authSlice.actions;
export default authSlice.reducer;
